/**
 * PDF Fill Service
 *
 * Generic service that works with any PDF template configuration.
 * Loads JSON config, fills PDF form fields, and triggers download.
 */

import {
  loadTemplateConfig,
  mapContactToPdfFields,
  type ContactLike,
} from "@/lib/utils/export/pdf-field-mapping";
import { logger } from "@/lib/logger";

/**
 * Fill a PDF form field with appropriate type handling
 * Note: This function is now only used server-side in the API route
 */
function fillPdfField(form: any, fieldName: string, value: string, fieldType: string): void {
  try {
    const field = form.getField(fieldName);

    if (!field) {
      return; // Field doesn't exist, skip
    }

    if (fieldType === "checkbox") {
      // For checkboxes, set checked if value is truthy
      if (
        value &&
        (value.toLowerCase() === "true" || value.toLowerCase() === "yes" || value === "1")
      ) {
        field.check();
      } else {
        field.uncheck();
      }
    } else if (fieldType === "select") {
      // For select/dropdown fields
      try {
        // Try to select the option
        if (value) {
          // pdf-lib dropdowns use select() method
          if (typeof field.select === "function") {
            // Try to find matching option
            const options = field.getOptions();
            const matchingOption = options.find(
              (opt: string) => opt.toLowerCase() === value.toLowerCase()
            );
            if (matchingOption) {
              field.select(matchingOption);
            } else if (options.length > 0) {
              // Use first option as fallback
              field.select(options[0]);
            }
          } else {
            // Fallback to text field behavior
            field.setText(value);
          }
        }
      } catch {
        // If select fails, try text
        try {
          field.setText(value);
        } catch {
          // Ignore if field doesn't support text
        }
      }
    } else {
      // Text field
      try {
        field.setText(value || "");
      } catch {
        // Ignore if field doesn't support text
      }
    }
  } catch (error) {
    // Silently skip fields that can't be filled
    logger.warn(`Failed to fill field ${fieldName}`, { error });
  }
}

/**
 * Generic function to fill PDF form with contact data
 * Uses server-side API route to avoid client-side pdf-lib compatibility issues
 */
export async function fillPdfForm(templateId: string, contact: any): Promise<void> {
  try {
    // Load template configuration for display name
    const config = await loadTemplateConfig(templateId);

    // Call server-side API route
    const response = await fetch("/api/pdf/fill", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies for authentication
      body: JSON.stringify({
        templateId,
        contact
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
      throw new Error(errorData.error || `Failed to fill PDF: ${response.statusText}`);
    }

    // Get PDF blob from response
    const pdfBlob = await response.blob();

    // Trigger download
    downloadFilledPdf(
      await pdfBlob.arrayBuffer(),
      `${config.displayName}_${contact.name || "contact"}.pdf`
    );
  } catch (error: any) {
    logger.error("Error filling PDF form", error);
    throw error;
  }
}

/**
 * Trigger browser download of filled PDF
 */
function downloadFilledPdf(pdfBytes: Uint8Array | ArrayBuffer, filename: string): void {
  // Ensure we have a proper Uint8Array
  let bytes: Uint8Array;
  if (pdfBytes instanceof ArrayBuffer) {
    bytes = new Uint8Array(pdfBytes);
  } else {
    bytes = pdfBytes;
  }
  // Type assertion needed for strict TypeScript
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
